import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAppDispatch } from '../../hooks';

import { MandateActions } from '../../actions/mandate/mandate.actions';

import type { Mandate } from '../../interfaces/client/mandate.interface';

import ProgressBar from './ProgressBar';
import { deriveProgress } from '../lib/mandate-helpers';

// =============================================================================
//  Inline progress editor — popover anchored to the ProgressBar on dashboard
//  / mandates list rows.
//
//  UX:
//    • Click the bar → popover with slider + number input opens.
//    • Drag the slider → live preview, no save yet.
//    • Release the slider OR press Enter / change-blur the number input
//      → save and close.
//    • Click outside or Escape → cancel without saving.
//
//  Same portal + manual-positioning approach as InlineStagePicker.
// =============================================================================

interface Props {
  mandate: Mandate;
}

const InlineProgressEditor = ({ mandate }: Props) => {
  const dispatch = useAppDispatch();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const numRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [draft, setDraft] = useState<number>(deriveProgress(mandate) ?? 0);
  const [saving, setSaving] = useState(false);

  // Reset draft to the latest persisted value whenever the popover opens.
  useEffect(() => {
    if (open) setDraft(deriveProgress(mandate) ?? 0);
  }, [open, mandate]);

  // Anchor below the bar when opening.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
  }, [open]);

  // Close on outside click + Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (triggerRef.current?.contains(target)) return;
      if (target.closest('[data-v3-progress-popover]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const commit = (nextValue: number) => {
    const v = Math.max(0, Math.min(100, Math.round(nextValue)));
    if (v === deriveProgress(mandate)) {
      setOpen(false);
      return;
    }
    setSaving(true);
    dispatch(
      MandateActions.updateMandate(
        mandate.id as number | string,
        { progress: v } as Partial<Mandate> as Mandate,
        () => {
          setSaving(false);
          setOpen(false);
        }
      )
    );
  };

  const portalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="progress-picker-trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (saving) return;
          setOpen((prev) => !prev);
        }}
        title="Change progress"
        aria-label={`Mandate progress (${deriveProgress(mandate)}%) — click to edit`}
      >
        <ProgressBar value={deriveProgress(mandate)} />
      </button>

      {open &&
        pos &&
        portalRoot &&
        createPortal(
          <div
            data-v3="true"
            data-v3-progress-popover="true"
            className="progress-popover"
            style={{ top: pos.top, left: pos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row">
              <input
                type="range"
                min={0}
                max={100}
                value={draft}
                onChange={(e) => setDraft(Number(e.target.value))}
                onMouseUp={() => commit(draft)}
                onTouchEnd={() => commit(draft)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') commit(draft);
                }}
                disabled={saving}
                aria-label="Progress slider"
              />
              <input
                ref={numRef}
                type="number"
                className="num"
                min={0}
                max={100}
                value={draft}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) setDraft(Math.max(0, Math.min(100, n)));
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commit(draft);
                  }
                }}
                disabled={saving}
                aria-label="Progress percent"
              />
            </div>
            <div className="hint">
              {saving ? 'Saving…' : 'Drag to preview; release (or press Enter) to save'}
            </div>
          </div>,
          portalRoot
        )}
    </>
  );
};

export default InlineProgressEditor;
